<template>
  <div class="coupons">
    <!-- 页头 -->
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">{{ $t("coupons.title") }}</h1>
        <div class="vtm-page-head__sub">{{ $t("coupons.subtitle") }}</div>
      </div>
      <div class="coupons-head-actions">
        <el-button size="small" v-hasPerm="['user:coupons:export']" icon="el-icon-receiving" @click="exportCoupons">{{
          $t("common.export")
        }}</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate()">{{ $t("coupons.create") }}</el-button>
      </div>
    </div>

    <!-- KPI -->
    <section class="coupons-kpis">
      <stat-card icon="el-icon-discount" :label="$t('coupons.kpiTotal')" :value="page.totalSize || 0"></stat-card>
      <stat-card tone="ok" icon="el-icon-success" :label="$t('coupons.kpiActive')" :value="kpiActive"></stat-card>
      <stat-card tone="warn" icon="el-icon-remove-outline" :label="$t('coupons.kpiExpired')" :value="kpiExpired"></stat-card>
    </section>

    <!-- 列表 -->
    <list-page
      :loading="loading"
      :error="loadError"
      :empty="!productList.length"
      :empty-text="$t('coupons.empty')"
      :total="page.totalSize || 0"
      :page-index="page.pageIndex"
      :page-size="page.pageSize"
      @page-change="currentChange"
      @size-change="sizeChange"
      @retry="currentChange(page.pageIndex)"
    >
      <vtm-table
        table-id="coupons"
        :columns="tableColumns"
        :data="productList"
        :loading="loading"
        :search-fields="['name']"
        :search-placeholder="$t('coupons.searchPlaceholder')"
        max-height="620"
        :border="true"
      >
        <template #toolbar-left>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate()">{{ $t("coupons.create") }}</el-button>
        </template>
        <template #mode="scope">
          <span :class="['vtm-pill', isPercentRow(scope.row) ? 'vtm-pill--info' : 'vtm-pill--ok']">{{ typeLabel(scope.row) }}</span>
        </template>
        <template #price="scope">
          <span class="vtm-num">{{ valueFmt(scope.row) }}</span>
        </template>
        <template #reachPrice="scope">
          <span class="vtm-num">{{ minSpendFmt(scope.row) }}</span>
        </template>
        <template #exTime="scope">
          <span>{{ scope.row.exTime || $t("coupons.noExpiry") }}</span>
        </template>
        <template #append>
          <el-table-column fixed="right" :label="$t('common.operate')" :width="$i18n.locale == 'zh_CN' ? 220 : 260" align="center">
            <template slot-scope="scope">
              <el-button size="mini" icon="el-icon-tickets" @click="showCouponCode(scope.row)">{{ $t("coupons.codes") }}</el-button>
              <el-button size="mini" icon="el-icon-copy-document" @click="openCreate(scope.row)">{{ $t("coupons.duplicate") }}</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="delCoupon(scope.row)">{{ $t("common.delete") }}</el-button>
            </template>
          </el-table-column>
        </template>
      </vtm-table>
    </list-page>

    <!-- 创建 / 复制优惠券 -->
    <el-drawer :visible.sync="createVisible" :with-header="false" size="460px" :append-to-body="true" @closed="resetForm">
      <div class="cp">
        <div class="cp__top">
          <div class="cp__title">
            <i class="el-icon-discount"></i>
            <span>{{ duplicating ? $t("coupons.duplicateTitle") : $t("coupons.createTitle") }}</span>
          </div>
          <el-button type="text" icon="el-icon-close" @click="createVisible = false"></el-button>
        </div>

        <div class="cp__body">
          <el-form ref="couponsForm" :model="couponsForm" :rules="rules" label-position="top">
            <el-form-item :label="$t('coupons.name')" prop="name">
              <el-input v-model="couponsForm.name" :placeholder="$t('coupons.namePlaceholder')"></el-input>
            </el-form-item>

            <el-form-item :label="$t('coupons.type')" prop="mode">
              <el-radio-group v-model="couponsForm.mode" size="small" class="cp__seg">
                <el-radio-button v-for="item in couponsmode" :key="item.id" :label="item.value">{{ item.label }}</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <div class="cp__row">
              <el-form-item :label="isPercent ? $t('coupons.valuePercent') : $t('coupons.valueAmount')" prop="price">
                <el-input-number
                  v-model="couponsForm.price"
                  :min="0"
                  :max="isPercent ? 100 : undefined"
                  :precision="isPercent ? 0 : 2"
                  :step="isPercent ? 5 : 0.5"
                  controls-position="right"
                  class="cp__num"
                ></el-input-number>
                <span class="cp__unit">{{ isPercent ? "%" : "$" }}</span>
              </el-form-item>
              <el-form-item :label="$t('coupons.minSpend')">
                <el-input-number v-model="couponsForm.reachPrice" :min="0" :precision="2" :step="1" controls-position="right" class="cp__num"></el-input-number>
                <div class="cp__hint">{{ $t("coupons.minSpendHint") }}</div>
              </el-form-item>
            </div>

            <div class="cp__row">
              <el-form-item :label="$t('coupons.qty')" prop="num">
                <el-input-number v-model="couponsForm.num" :min="1" :precision="0" :step="10" controls-position="right" class="cp__num"></el-input-number>
              </el-form-item>
              <el-form-item :label="$t('coupons.expires')">
                <el-date-picker
                  v-model="couponsForm.exTime"
                  type="datetime"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  :placeholder="$t('coupons.expiresPlaceholder')"
                  style="width: 100%"
                ></el-date-picker>
              </el-form-item>
            </div>

            <el-form-item :label="$t('coupons.codeMode')">
              <el-radio-group v-model="couponsForm.couponCodeFlag" size="small" class="cp__seg">
                <el-radio-button :label="true">{{ $t("coupons.codeAuto") }}</el-radio-button>
                <el-radio-button :label="false">{{ $t("coupons.codeCustom") }}</el-radio-button>
              </el-radio-group>
              <div class="cp__hint">{{ couponsForm.couponCodeFlag ? $t("coupons.codeAutoHint") : $t("coupons.codeCustomHint") }}</div>
            </el-form-item>

            <el-form-item v-if="!couponsForm.couponCodeFlag" :label="$t('coupons.code')" prop="couponCode">
              <el-input v-model="couponsForm.couponCode" :placeholder="$t('coupons.codePlaceholder')"></el-input>
            </el-form-item>
          </el-form>
        </div>

        <div class="cp__footer">
          <el-button size="small" @click="createVisible = false">{{ $t("common.cancel") }}</el-button>
          <el-button size="small" type="primary" :loading="submitting" @click="submitForm">{{ $t("coupons.createBtn") }}</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- 券码列表 -->
    <el-drawer :title="showTitle" :visible.sync="showTable" :append-to-body="true" direction="rtl" size="420px">
      <div class="cp-codes">
        <div class="cp-codes__bar">
          <span class="cp-codes__for">{{ codesFor }}</span>
          <el-button size="small" v-hasPerm="['user:coupons:export']" icon="el-icon-receiving" @click="exportCouponsMa">{{
            $t("common.export")
          }}</el-button>
        </div>
        <el-table
          v-loading="codeloading"
          :empty-text="$t('common.noData')"
          :element-loading-text="$t('common.loading')"
          :data="codeData"
          style="width: 100%"
        >
          <el-table-column property="code" :label="$t('coupons.codeCol')"></el-table-column>
          <el-table-column :label="$t('coupons.statusCol')" width="120" align="center">
            <template slot-scope="scope">
              <span :class="['vtm-pill', String(scope.row.isUse) === '1' ? 'vtm-pill--off' : 'vtm-pill--ok']">{{ funcState(scope.row) }}</span>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          background
          layout="prev, pager, next"
          :current-page="codepage.pageIndex"
          :total="codepage.totalSize"
          :page-size="codepage.pageSize"
          @current-change="currentChangeCode"
        ></el-pagination>
      </div>
    </el-drawer>
  </div>
</template>

<script>
  // 优惠券中心：清晰的列表 + 引导式「创建/复制」抽屉 + 券码抽屉。
  // 后端契约保持不变：discount/insert|page|delete、discountCode/page、discount(Code)/export；
  // 类型/状态取自字典 coupon_type / coupon_state；百分比类型 = mode 值为 2。
  // 说明：后端仅提供新增+删除（无更新接口），故「编辑」以「复制」形式提供。
  import ListPage from "_c/common/ListPage.vue";
  import VtmTable from "_c/common/VtmTable.vue";
  import StatCard from "_c/common/StatCard.vue";

  const emptyForm = () => ({
    name: "",
    reachPrice: 0,
    mode: "",
    price: 0,
    exTime: "",
    num: 1,
    couponCodeFlag: true,
    couponCode: ""
  });

  export default {
    name: "Coupons",
    components: { ListPage, VtmTable, StatCard },
    data() {
      return {
        search: { name: "" },
        loading: false,
        loadError: false,
        page: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        productList: [],
        // 创建/复制抽屉
        createVisible: false,
        duplicating: false,
        submitting: false,
        couponsForm: emptyForm(),
        rules: {
          name: [{ required: true, message: this.$t("coupons.namePlaceholder"), trigger: "blur" }],
          mode: [{ required: true, message: this.$t("coupons.typeRequired"), trigger: "change" }],
          price: [{ required: true, message: this.$t("coupons.valueRequired"), trigger: "blur" }],
          num: [{ required: true, message: this.$t("coupons.qtyRequired"), trigger: "blur" }],
          couponCode: [{ required: true, message: this.$t("coupons.codeRequired"), trigger: "blur" }]
        },
        couponsmode: [],
        couponsState: [],
        // 券码抽屉
        showTitle: this.$t("coupons.codes"),
        codesFor: "",
        showTable: false,
        codeData: [],
        codeloading: false,
        codepage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        codesearch: { discountId: 0 }
      };
    },
    computed: {
      isPercent() {
        return String(this.couponsForm.mode) === "2";
      },
      tableColumns() {
        return [
          { prop: "name", label: this.$t("coupons.name"), minWidth: 180 },
          { prop: "mode", label: this.$t("coupons.type"), width: 130, align: "center", tooltip: false },
          { prop: "price", label: this.$t("coupons.value"), width: 110, align: "right" },
          { prop: "reachPrice", label: this.$t("coupons.minSpend"), width: 130, align: "right" },
          { prop: "num", label: this.$t("coupons.issued"), width: 100, align: "right" },
          { prop: "overNum", label: this.$t("coupons.remaining"), width: 120, align: "right" },
          { prop: "exTime", label: this.$t("coupons.expires"), width: 170, align: "center", tooltip: false },
          { prop: "createTime", label: this.$t("coupons.created"), width: 160, align: "center", hidden: true }
        ];
      },
      kpiActive() {
        const now = Date.now();
        return this.productList.filter(c => !c.exTime || this.ts(c.exTime) > now).length;
      },
      kpiExpired() {
        const now = Date.now();
        return this.productList.filter(c => c.exTime && this.ts(c.exTime) <= now).length;
      }
    },
    mounted() {
      this.couponState();
      this.couponmode();
      this.currentChange();
    },
    methods: {
      ts(t) {
        return new Date(String(t).replace(/-/g, "/")).getTime() || 0;
      },
      isPercentRow(row) {
        return String(row.mode) === "2";
      },
      typeLabel(row) {
        return this.$cloud.getDictLabel(this.couponsmode, parseInt(row.mode)) || "-";
      },
      funcState(row) {
        return this.$cloud.getDictLabel(this.couponsState, parseInt(row.isUse)) || "-";
      },
      valueFmt(row) {
        return this.isPercentRow(row) ? `${row.price}%` : `$${row.price}`;
      },
      minSpendFmt(row) {
        return Number(row.reachPrice) > 0 ? `$${row.reachPrice}` : this.$t("coupons.noMin");
      },
      // ---- 创建 / 复制 ----
      openCreate(row) {
        this.couponsForm = emptyForm();
        if (row) {
          this.duplicating = true;
          // 复制：保留规则，清空唯一码 → 生成随机码，避免自定义码冲突
          this.couponsForm.name = row.name || "";
          this.couponsForm.mode = row.mode !== undefined && row.mode !== null ? row.mode : "";
          this.couponsForm.price = Number(row.price) || 0;
          this.couponsForm.reachPrice = Number(row.reachPrice) || 0;
          this.couponsForm.num = Number(row.num) || 1;
          this.couponsForm.exTime = row.exTime || "";
          this.couponsForm.couponCodeFlag = true;
          this.couponsForm.couponCode = "";
        } else {
          this.duplicating = false;
        }
        this.createVisible = true;
        this.$nextTick(() => this.$refs.couponsForm && this.$refs.couponsForm.clearValidate());
      },
      resetForm() {
        this.couponsForm = emptyForm();
        this.duplicating = false;
        if (this.$refs.couponsForm) {
          this.$refs.couponsForm.clearValidate();
        }
      },
      submitForm() {
        this.$refs.couponsForm.validate(valid => {
          if (!valid) {
            return;
          }
          const payload = {
            name: this.couponsForm.name,
            reachPrice: Number(this.couponsForm.reachPrice) || 0,
            mode: this.couponsForm.mode,
            price: Number(this.couponsForm.price) || 0,
            exTime: this.couponsForm.exTime || "",
            num: Number(this.couponsForm.num) || 0,
            couponCodeFlag: this.couponsForm.couponCodeFlag,
            couponCode: this.couponsForm.couponCodeFlag ? "" : this.couponsForm.couponCode
          };
          this.submitting = true;
          this.$api
            .request(this.$url.Product.insertCoupons, this.$method.post, payload)
            .then(res => {
              this.submitting = false;
              if (res.code === 0) {
                this.$message.success(this.$t("product.addSuccess"));
                this.createVisible = false;
                this.currentChange(1);
              } else {
                this.$message.warning(res.msg || this.$t("product.addFail"));
              }
            })
            .catch(() => {
              this.submitting = false;
              this.$message.warning(this.$t("product.addFail"));
            });
        });
      },
      // ---- 列表 ----
      sizeChange(pageSize) {
        this.page.pageSize = pageSize;
        this.currentChange();
      },
      currentChange(pageIndex) {
        this.loading = true;
        this.loadError = false;
        this.page.pageIndex = pageIndex ? pageIndex : 1;
        const obj = this.$cloud.objMerge(this.search, this.page);
        this.$api
          .request(this.$url.Product.getCoupons, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.productList = res.data.records || [];
              this.page.totalSize = parseInt(res.data.total) || 0;
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
            this.loadError = true;
          });
      },
      delCoupon(row) {
        this.$confirm(this.$t("product.deleteCoupon"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api.request(this.$url.Product.deleteCoupon, this.$method.delete, { id: row.id }).then(res => {
            if (res.code === 0) {
              this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
              this.currentChange(this.page.pageIndex);
            }
          });
        });
      },
      // ---- 券码 ----
      showCouponCode(row) {
        this.codesearch.discountId = row.id;
        this.codesFor = row.name;
        this.showTitle = this.$t("coupons.codesFor", { name: row.name });
        this.currentChangeCode();
        this.showTable = true;
      },
      currentChangeCode(pageIndex) {
        this.codeloading = true;
        this.codepage.pageIndex = pageIndex ? pageIndex : 1;
        const obj = this.$cloud.objMerge(this.codesearch, this.codepage);
        this.$api
          .request(this.$url.Product.getCouponsCode, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.codeData = res.data.records || [];
              this.codepage.totalSize = parseInt(res.data.total) || 0;
            }
            this.codeloading = false;
          })
          .catch(() => {
            this.codeloading = false;
          });
      },
      // ---- 字典 ----
      couponState() {
        this.$api.dict("coupon_state").then(res => {
          if (res.code === 0) {
            this.couponsState = res.data;
          }
        });
      },
      couponmode() {
        this.$api.dict("coupon_type").then(res => {
          if (res.code === 0) {
            this.couponsmode = res.data;
          }
        });
      },
      // ---- 导出 ----
      exportCoupons() {
        this.$confirm(this.$t("product.export"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => this.$api.download(this.$url.Product.exportCoupon, this.search))
          .catch(err => err && err.msg && this.$message.error(err.msg));
      },
      exportCouponsMa() {
        this.$confirm(this.$t("product.exportMa"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => this.$api.download(this.$url.Product.exportCouponMa, { discountId: this.codesearch.discountId }))
          .catch(err => err && err.msg && this.$message.error(err.msg));
      }
    }
  };
</script>

<style scoped lang="scss">
  .coupons-head-actions {
    display: flex;
    gap: 8px;
  }
  .coupons-kpis {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    padding: 0 0 14px;
  }

  /* 创建抽屉 */
  .cp {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--vtm-bg);
  }
  .cp__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    background: var(--vtm-surface);
    border-bottom: 1px solid var(--vtm-line);
  }
  .cp__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--vtm-display);
    font-size: 16px;
    font-weight: 600;
    color: var(--vtm-heading);

    i {
      color: var(--vtm-mint-ink);
    }
  }
  .cp__body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 18px;
  }
  .cp__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .cp__num {
    width: 100%;
  }
  .cp__unit {
    position: absolute;
    right: 34px;
    top: 0;
    color: var(--vtm-ink-3);
    font-size: 12px;
    pointer-events: none;
  }
  .cp__hint {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
    line-height: 1.4;
    margin-top: 2px;
  }
  .cp__seg ::v-deep .el-radio-button__inner {
    font-weight: 500;
  }
  .cp__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    background: var(--vtm-surface);
    border-top: 1px solid var(--vtm-line);
  }

  /* 券码抽屉 */
  .cp-codes {
    padding: 0 16px 16px;
  }
  .cp-codes__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 6px 0 12px;
  }
  .cp-codes__for {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cp-codes ::v-deep .el-pagination {
    text-align: center;
    margin-top: 12px;
  }
</style>
